import {
  call, takeLatest, put, select, delay, take,
  takeLeading,
} from 'redux-saga/effects';
import { api } from '@/utils/api';
import preChatActions from './preChat.actions';
import { PreChatActionTypes } from './preChat.types';
import preChatAssembly from './preChat.assembly';
import alertsAssembly from '../alerts/alerts.assembly';
import { config } from '@/config';
import { isBRServer } from '@/utils/Validators/validation';
import { alertsActions } from '../alerts';
import { domainsActions } from '../domains';
import logger from '@/utils/logger';
import { ATIVACAO_ALERT_TEMP_DOMAIN, ATIVACAO_WP_MIGRATION_ALERT } from '@/config/GrowthBook/constants';
import { DomainsActionTypes } from '../domains/domains.types';

const {
  AWS_GATOR_SOLUTIONS_URL,
  WHMCS_URL,
} = config;

function* getDiagnostic({ payload }) {
  const {
    enabledSubscriptionInDiagnostic,
    enablePendingOrders,
    enabledCloudflare,
    enableAlertCreditRefund,
    enabledCloudflareDelayedAlert,
  } = payload;
  yield put(preChatActions.diagnostic.loading(true));


  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const sitesState = yield select(state => state.sites);

  const sitesList = sitesState.list;
  const siteWithCloudflare = sitesList.find(site => site.cloudflare_available && site.status === 'Active');

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/diagnostic`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  try {
    const { toggles } = yield select(state => state.common);

    if (toggles && toggles[ATIVACAO_ALERT_TEMP_DOMAIN]) {
      yield put(domainsActions.temporaryDomains.request());
    }

    const tempDomainExpectedTypes = [
      DomainsActionTypes.GET_TEMPORARY_DOMAINS_SUCCESS,
      DomainsActionTypes.GET_TEMPORARY_DOMAINS_FAILURE,
    ];

    let tempDomainResponse = null;
    if (toggles && toggles[ATIVACAO_ALERT_TEMP_DOMAIN]) {
      tempDomainResponse = yield take(tempDomainExpectedTypes);
    }

    if (
      !toggles[ATIVACAO_ALERT_TEMP_DOMAIN]
      || tempDomainExpectedTypes.includes(tempDomainResponse.type)
    ) {
      const { temporaryDomains } = yield select(state => state.newDomains);
      const response = yield call(api.aws.get, request);
      const diagnostic = preChatAssembly.diagnosticResponse(response);
      yield put(preChatActions.diagnostic.set(diagnostic));
      const diagnosticAlerts = alertsAssembly.mountDiagnosticAlerts({
        diagnostic,
        enabledCloudflare,
        siteWithCloudflare,
        enabledSubscriptionInDiagnostic,
        enablePendingOrders,
        enableAlertCreditRefund,
        enabledCloudflareDelayedAlert,
        temporaryDomains,
        enabledTempDomainAlert: toggles[ATIVACAO_ALERT_TEMP_DOMAIN],
        withMigrationAlert: toggles[ATIVACAO_WP_MIGRATION_ALERT],
      });

      yield put(alertsActions.set.diagnosticAlerts(diagnosticAlerts));

      const domainsPropagation = alertsAssembly.getDomainsPropagations(diagnostic);
      yield put(domainsActions.propagationStatus.set(domainsPropagation));

      yield put(preChatActions.diagnostic.success());
    }
  } catch (e) {
    const error = { ...e, userRequestData: request };
    if (e.response && e.response.status !== 401) {
      logger.error('Error sagas getDiagnostic', error);
    }

    yield put(preChatActions.diagnostic.failure());
    yield delay(2000);
  }
  yield put(preChatActions.diagnostic.loading(false));
}

function* getProductsByStatus(action) {
  const { withCancelled, withPending } = action.payload;

  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v2/clientarea/br/products-by-status?status=Active,Suspended,Expired${withCancelled ? ',Cancelled,Terminated' : ''}${withPending ? ',Pending' : ''}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(preChatActions.productByStatus.loading(true));

  try {
    const response = yield call(api.aws.get, request);
    const productsList = response.data && response.data.data;
    yield put(preChatActions.productByStatus.set(productsList));
    yield put(preChatActions.productByStatus.success());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas getProductsByStatus', error);
    yield put(preChatActions.productByStatus.failure());
  }

  yield put(preChatActions.productByStatus.loading(false));
}

function* getProductDetails({ payload }) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/diagnostic/${payload && payload.productType}/${payload && payload.id}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  yield put(preChatActions.productDetails.loading(true));

  try {
    const response = yield call(api.aws.get, request);
    const productDetails = preChatAssembly.productDetailsResponse(response.data);
    yield put(preChatActions.productDetails.set(productDetails));
    yield put(preChatActions.productDetails.success());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas getProductDetails', error);
    yield put(preChatActions.productDetails.failure());
    yield delay(2000);
  }
  yield put(preChatActions.productDetails.loading(false));
}

function* getWeekDay() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/current-time`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  try {
    const response = yield call(api.aws.get, request);
    const weekDay = preChatAssembly.getWeekDayResponse(response);
    yield put(preChatActions.weekDay.set(weekDay));
    yield put(preChatActions.weekDay.success());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas getWeekDay', error);
    yield put(preChatActions.weekDay.failure());
  }
}

function* startChatbot() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${WHMCS_URL}/api/v3/cortex/start-chat`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.post, request);
    yield put(preChatActions.chatbot.setSession(response.data && response.data.session_id));
    yield put(preChatActions.chatbot.startSuccess());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas startChatbot', error);
    yield put(preChatActions.chatbot.startFailure());

    yield delay(3000);
    yield call(startChatbot);
  }
}

function* sendMessageToChatbot({ payload }) {
  const { message, retry } = payload;
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { chatbotSessionID } = yield select(state => state.preChat);

  const request = {
    url: `${WHMCS_URL}/api/v3/cortex/message/${chatbotSessionID}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
    },
    data: { question: message },
  };

  try {
    let transferToHuman = false;
    const response = yield call(api.whmcs.post, request);

    if (response.data && response.data.length) {
      response.data.forEach((item) => {
        if (item.progress === 'transfer_to_human_assistant') {
          transferToHuman = true;
        }
      });
    }

    const { answer, id } = preChatAssembly.getBotResponse(response.data);

    yield put(preChatActions.chatbot.setChatbotMessage({
      text: answer, origin: 'bot', id, transferToHuman,
    }));
    yield put(preChatActions.chatbot.sendMessageSuccess());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas sendMessageToChatbot', error);
    if (retry) {
      yield put(preChatActions.chatbot.setChatbotMessage({ error: 'retry', message }));
    } else {
      yield put(preChatActions.chatbot.setChatbotMessage({ error: 'first', message }));
    }
    yield put(preChatActions.chatbot.sendMessageFailure());
  }
}

function* evaluateAnswer({ payload }) {
  const { evaluation, messageId } = payload;
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${WHMCS_URL}/api/v3/cortex/evaluate/${messageId}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
    },
    data: { type: evaluation },
  };

  try {
    yield call(api.whmcs.post, request);
    yield put(preChatActions.chatbot.evaluateAnswerSuccess());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas evaluateAnswer', error);

    yield put(preChatActions.chatbot.evaluateAnswerFailure());
  }
}

function* startChatControl() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${WHMCS_URL}/api/v3/chat-control`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
    },
  };

  try {
    yield call(api.whmcs.post, request);
    yield put(preChatActions.chat.startSuccess());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas startChatControl', error);
    yield put(preChatActions.chat.startFailure());
  }
}

function* finishChatControl() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${WHMCS_URL}/api/v3/chat-control/close`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
    },
  };

  try {
    yield call(api.whmcs.post, request);
    yield put(preChatActions.chat.finishSuccess());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas finishChatControl', error);
    yield put(preChatActions.chat.finishFailure());
  }
}

function* requestChatStatus() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${WHMCS_URL}/api/v3/chat-control`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);

    if (response && response.data && response.data.status === 'open') {
      yield put(preChatActions.chat.setActiveChat(true));
    }

    if (response && response.data && response.data.status === 'closed') {
      yield put(preChatActions.chat.setActiveChat(false));
    }
    yield put(preChatActions.chat.statusSuccess());
  } catch (e) {
    if (e.response && e.response.statusText !== 'Not Found') {
      const error = { ...e, userRequestData: request };
      logger.error('Error sagas requestChatStatus', error);
    }
    yield put(preChatActions.chat.statusFailure());
  }
}

function* sagas() {
  yield takeLatest(PreChatActionTypes.GET_DIAGNOSTIC_REQUEST, getDiagnostic);
  yield takeLatest(PreChatActionTypes.PRODUCT_BY_STATUS_REQUEST, getProductsByStatus);
  yield takeLatest(PreChatActionTypes.GET_PRODUCT_DETAILS_REQUEST, getProductDetails);
  yield takeLatest(PreChatActionTypes.GET_WEEK_DAY_REQUEST, getWeekDay);
  yield takeLatest(PreChatActionTypes.START_CHATBOT_REQUEST, startChatbot);
  yield takeLatest(PreChatActionTypes.SEND_CHATBOT_MESSAGE_REQUEST, sendMessageToChatbot);
  yield takeLatest(PreChatActionTypes.EVALUATE_CHATBOT_ANSWER_REQUEST, evaluateAnswer);
  yield takeLatest(PreChatActionTypes.START_CHAT_SESSION_REQUEST, startChatControl);
  yield takeLeading(PreChatActionTypes.FINISH_CHAT_SESSION_REQUEST, finishChatControl);
  yield takeLatest(PreChatActionTypes.CHAT_STATUS_REQUEST, requestChatStatus);
}

export default sagas;
